import { BookOpen } from 'lucide-react';
import { useMemo, useState, type JSX } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useTargets } from '@/features/memorization/hooks';
import type { TargetStatus, TargetView } from '@/features/memorization/types';
import { useSurahs } from '@/features/quran/hooks';
import type { SurahView } from '@/features/quran/types';

/**
 * "Peta Hafalan" - a whole-mushaf overview of where a student's targets
 * sit across all 114 surahs.
 *
 * Built entirely from data this platform already owns: the structural
 * Quran reference (`GET /quran/surahs`, ADR-023 - surah numbers, names and
 * ayah counts, no verse text) and the student's own targets. It needs no
 * Quran text licence and no external Quran API, so it works today and
 * keeps working unchanged if a licensed text source is added later.
 *
 * What a tile means is deliberately narrow: it reflects the ayah ranges
 * this student has been *assigned*, and which of those ranges carry a
 * COMPLETED target status. It is not an independent verification that the
 * student has memorized those ayahs - the authoritative, live per-target
 * figure is `GET /memorization/targets/:id/coverage`, which the target
 * detail page already shows. The caption below the grid says exactly this
 * rather than letting a colourful grid imply more precision than the
 * numbers behind it carry.
 *
 * Each tile is a button, not a decorated `div`. That is a correctness fix
 * rather than polish: a tile's only identification was `title`/`aria-label`,
 * and a phone has no hover, so all 114 surah names were unreachable for the
 * touch users this PWA is primarily built for. Tapping a tile now names the
 * surah in a detail panel under the grid.
 */

/** `GET /memorization/targets` caps `limit` at 100 (server validation). */
const MAP_TARGET_PAGE_SIZE = 100;

/**
 * Statuses that no longer describe live work on a range (ADR-021). Drawn
 * as untouched rather than as progress - a cancelled target is not a
 * partial achievement.
 */
const INERT_STATUSES: ReadonlySet<TargetStatus> = new Set<TargetStatus>(['CANCELLED', 'EXPIRED']);

interface AyahRange {
  readonly start: number;
  readonly end: number;
}

interface SurahProgress {
  readonly surah: SurahView;
  readonly assignedAyahs: number;
  readonly completedAyahs: number;
}

/**
 * Total distinct ayahs spanned by `ranges`, counting an overlap once.
 * Two targets over ayah 1-10 and 5-15 cover 15 ayahs, never 25 - without
 * this, a re-assigned or overlapping range would inflate the map past a
 * surah's real length.
 */
export function coveredAyahCount(ranges: readonly AyahRange[]): number {
  const sorted = [...ranges].sort((left, right) => left.start - right.start);
  let total = 0;
  let openStart: number | null = null;
  let openEnd = 0;

  for (const range of sorted) {
    if (openStart === null) {
      openStart = range.start;
      openEnd = range.end;
      continue;
    }
    // `<= openEnd + 1` also merges directly adjacent ranges (1-5 and
    // 6-10 are ten contiguous ayahs, not two separate spans).
    if (range.start <= openEnd + 1) {
      openEnd = Math.max(openEnd, range.end);
      continue;
    }
    total += openEnd - openStart + 1;
    openStart = range.start;
    openEnd = range.end;
  }

  return openStart === null ? 0 : total + (openEnd - openStart + 1);
}

/** Groups `targets` by surah number and reduces each group to covered-ayah counts. */
export function buildSurahProgress(
  surahs: readonly SurahView[],
  targets: readonly TargetView[],
): readonly SurahProgress[] {
  const assignedBySurah = new Map<number, AyahRange[]>();
  const completedBySurah = new Map<number, AyahRange[]>();

  for (const target of targets) {
    if (INERT_STATUSES.has(target.status)) {
      continue;
    }
    const range: AyahRange = { start: target.startAyah, end: target.endAyah };
    const assigned = assignedBySurah.get(target.surah) ?? [];
    assigned.push(range);
    assignedBySurah.set(target.surah, assigned);

    if (target.status === 'COMPLETED') {
      const completed = completedBySurah.get(target.surah) ?? [];
      completed.push(range);
      completedBySurah.set(target.surah, completed);
    }
  }

  return surahs.map((surah) => ({
    surah,
    // Clamped to the surah's real length: a stale or malformed range can
    // never render as more than 100% of a surah.
    assignedAyahs: Math.min(
      coveredAyahCount(assignedBySurah.get(surah.number) ?? []),
      surah.ayahCount,
    ),
    completedAyahs: Math.min(
      coveredAyahCount(completedBySurah.get(surah.number) ?? []),
      surah.ayahCount,
    ),
  }));
}

/** Share of a surah carrying COMPLETED targets, 0..1. */
function completedRatio(entry: SurahProgress): number {
  return entry.surah.ayahCount === 0 ? 0 : entry.completedAyahs / entry.surah.ayahCount;
}

/**
 * Fixed tint steps rather than an interpolated class name - Tailwind needs
 * every class to exist as a literal string at build time.
 *
 * Only the fully-completed step inverts its text. The partial steps stay on
 * `text-foreground` over light tints so the progress bar drawn inside a
 * partial tile (`bg-primary`) keeps a guaranteed contrast pair against its
 * own background; the previous `bg-primary/60` + `text-primary-foreground`
 * step put white on a mid-tone wash, which was already marginal for WCAG AA
 * at this 11px type size.
 */
function tileClasses(entry: SurahProgress): string {
  if (entry.assignedAyahs === 0) {
    return 'border-border bg-muted/60 text-muted-foreground';
  }
  const ratio = completedRatio(entry);
  if (ratio >= 1) {
    return 'border-primary bg-primary text-primary-foreground';
  }
  if (ratio >= 0.5) {
    return 'border-primary/60 bg-primary/40 text-foreground';
  }
  if (ratio > 0) {
    return 'border-primary/40 bg-primary/20 text-foreground';
  }
  return 'border-primary/30 bg-primary/10 text-foreground';
}

function tileLabel(entry: SurahProgress): string {
  const { surah, assignedAyahs, completedAyahs } = entry;
  const base = `${String(surah.number)}. ${surah.nameLatin} - ${String(surah.ayahCount)} ayat`;
  if (assignedAyahs === 0) {
    return `${base}, belum ada target`;
  }
  return `${base}, ${String(assignedAyahs)} ayat dalam target, ${String(completedAyahs)} ayat berstatus selesai`;
}

/**
 * Tiles fade in as a short wave across the mushaf instead of appearing all
 * at once. The step is deliberately tiny: at 114 tiles even 5ms each would
 * run for over half a second, so the delay is capped and the whole grid has
 * settled well inside a second. Pure CSS (`.tile-rise` in index.css, which
 * carries the `prefers-reduced-motion` opt-out) - per ADR-036, motion that
 * CSS can express does not justify pulling in an animation library.
 */
const STAGGER_STEP_MS = 5;
const STAGGER_MAX_MS = 360;

function SurahTile({
  entry,
  index,
  isSelected,
  onSelect,
}: {
  readonly entry: SurahProgress;
  readonly index: number;
  readonly isSelected: boolean;
  readonly onSelect: (surahNumber: number | null) => void;
}): JSX.Element {
  const ratio = completedRatio(entry);
  // Drawn only between the endpoints: an empty and a full tile already say
  // "0%" and "100%" through their tint, so a bar there would be noise.
  const showsBar = ratio > 0 && ratio < 1;
  const label = tileLabel(entry);

  return (
    <button
      type="button"
      onClick={() => {
        onSelect(isSelected ? null : entry.surah.number);
      }}
      aria-pressed={isSelected}
      aria-label={label}
      title={label}
      style={{ animationDelay: `${String(Math.min(index * STAGGER_STEP_MS, STAGGER_MAX_MS))}ms` }}
      // `outline`, not this codebase's usual `focus-visible:ring-*`: a ring
      // is a box-shadow, and `.tile-3d` declares its own `box-shadow` in
      // plain (unlayered) CSS, which outranks Tailwind's `@layer utilities`
      // and silently erases the ring - verified in the browser, where a
      // focused tile computed to the tile shadow alone. An invisible focus
      // indicator fails WCAG 2.4.7, so the state is drawn with a property
      // nothing here competes for.
      className={`tile-3d tile-rise relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border text-[0.6875rem] font-semibold tabular-nums outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring ${isSelected ? 'outline-2 outline-primary' : ''} ${tileClasses(entry)}`}
    >
      {entry.surah.number}
      {showsBar && (
        <span
          aria-hidden="true"
          className="absolute inset-x-0.5 bottom-0.5 h-[3px] overflow-hidden rounded-full bg-primary/25"
        >
          <span
            className="block h-full rounded-full bg-primary"
            style={{ width: `${String(ratio * 100)}%` }}
          />
        </span>
      )}
    </button>
  );
}

const REVELATION_PLACE_LABELS: Readonly<Record<string, string>> = {
  MECCAN: 'Makkiyah',
  MEDINAN: 'Madaniyah',
};

/**
 * The detail line under the grid. This is where a surah's name actually
 * becomes readable on a touch device - see the note at the top of this file.
 *
 * Not an `aria-live` region on purpose: the tile that opens it is a button
 * carrying the same text as its `aria-label`, so a screen reader already
 * announces the surah on focus. Making this live too would say it twice.
 */
function SurahDetail({ entry }: { readonly entry: SurahProgress | null }): JSX.Element {
  if (entry === null) {
    return (
      <p className="rounded-lg border border-dashed border-border/70 px-3 py-2.5 text-xs text-muted-foreground">
        Ketuk salah satu kotak untuk melihat nama surah dan rincian hafalannya.
      </p>
    );
  }

  const { surah, assignedAyahs, completedAyahs } = entry;
  const percentage = completedRatio(entry) * 100;
  const place = REVELATION_PLACE_LABELS[surah.revelationPlace] ?? surah.revelationPlace;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border/70 bg-secondary/30 px-3 py-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold">
          {surah.number}. {surah.nameLatin}
        </span>
        <span className="shrink-0 text-lg leading-none" lang="ar" dir="rtl">
          {surah.nameArabic}
        </span>
      </div>

      <div
        className="h-1.5 overflow-hidden rounded-full bg-primary/15"
        role="presentation"
        aria-hidden="true"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300"
          style={{ width: `${String(percentage)}%` }}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {place} &middot; {surah.ayahCount} ayat &middot;{' '}
        {assignedAyahs === 0
          ? 'belum ada target'
          : `${String(completedAyahs)} ayat selesai dari ${String(assignedAyahs)} ayat dalam target`}
      </p>
    </div>
  );
}

function SummaryFigure({
  value,
  label,
}: {
  readonly value: string;
  readonly label: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-2xl font-semibold tabular-nums">{value}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

const RING_RADIUS = 42;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * The headline figure as a ring rather than a bare percentage: at the
 * single-digit percentages a real student starts from, a number alone
 * carries no sense of scale, while an arc against a full circle does.
 * Plain SVG - no chart library added to the bundle for one shape.
 */
function ProgressRing({ percentage }: { readonly percentage: number }): JSX.Element {
  const clamped = Math.max(0, Math.min(100, percentage));
  return (
    <div className="relative flex size-28 shrink-0 items-center justify-center">
      <svg viewBox="0 0 100 100" className="size-28 -rotate-90" aria-hidden="true">
        <circle
          cx="50"
          cy="50"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="9"
          className="stroke-secondary"
        />
        <circle
          cx="50"
          cy="50"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="9"
          strokeLinecap="round"
          className="stroke-primary"
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={RING_CIRCUMFERENCE * (1 - clamped / 100)}
        />
      </svg>
      <span className="absolute flex flex-col items-center">
        <span className="text-xl font-semibold tabular-nums">{clamped.toFixed(1)}%</span>
        <span className="text-[0.625rem] text-muted-foreground">selesai</span>
      </span>
    </div>
  );
}

export function QuranProgressMap({ studentId }: { readonly studentId: string }): JSX.Element {
  const { data: surahs, isLoading: surahsLoading, isError: surahsError } = useSurahs();
  const {
    data: targets,
    isLoading: targetsLoading,
    isError: targetsError,
  } = useTargets(studentId, 1, MAP_TARGET_PAGE_SIZE);

  const isLoading = surahsLoading || targetsLoading;
  const isError = surahsError || targetsError;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BookOpen className="size-4 text-primary" aria-hidden="true" />
          Peta Hafalan
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading && <Skeleton className="h-48 w-full" />}

        {!isLoading && isError && (
          <p className="text-sm text-destructive">Gagal memuat peta hafalan.</p>
        )}

        {!isLoading && !isError && surahs !== undefined && targets !== undefined && (
          <QuranProgressMapContent surahs={surahs} targets={targets.items} />
        )}
      </CardContent>
    </Card>
  );
}

function QuranProgressMapContent({
  surahs,
  targets,
}: {
  readonly surahs: readonly SurahView[];
  readonly targets: readonly TargetView[];
}): JSX.Element {
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);

  // Selecting a tile re-renders the whole grid, so the 114-surah reduction
  // is memoized rather than recomputed on every tap.
  const entries = useMemo(() => buildSurahProgress(surahs, targets), [surahs, targets]);
  const selectedEntry = entries.find((entry) => entry.surah.number === selectedNumber) ?? null;

  const totalAyahs = surahs.reduce((sum, surah) => sum + surah.ayahCount, 0);
  const assignedAyahs = entries.reduce((sum, entry) => sum + entry.assignedAyahs, 0);
  const completedAyahs = entries.reduce((sum, entry) => sum + entry.completedAyahs, 0);
  const touchedSurahs = entries.filter((entry) => entry.assignedAyahs > 0).length;
  const completedPercentage = totalAyahs === 0 ? 0 : (completedAyahs / totalAyahs) * 100;

  return (
    <>
      <div className="flex flex-col items-center gap-5 rounded-lg border border-border/70 bg-secondary/30 p-4 sm:flex-row sm:gap-6">
        <ProgressRing percentage={completedPercentage} />
        <div className="grid flex-1 grid-cols-2 gap-4 sm:grid-cols-3">
          <SummaryFigure
            value={`${String(touchedSurahs)}/${String(surahs.length)}`}
            label="Surah tersentuh"
          />
          <SummaryFigure value={completedAyahs.toLocaleString('id-ID')} label="Ayat selesai" />
          <SummaryFigure value={assignedAyahs.toLocaleString('id-ID')} label="Ayat dalam target" />
          <div className="col-span-2 text-xs text-muted-foreground sm:col-span-3">
            dari {totalAyahs.toLocaleString('id-ID')} ayat dalam mushaf
          </div>
        </div>
      </div>

      <ul
        className="grid list-none grid-cols-8 gap-1.5 p-0 sm:grid-cols-12 lg:grid-cols-[repeat(19,minmax(0,1fr))]"
        aria-label="Peta hafalan per surah"
      >
        {entries.map((entry, index) => (
          <li key={entry.surah.id}>
            <SurahTile
              entry={entry}
              index={index}
              isSelected={entry.surah.number === selectedNumber}
              onSelect={setSelectedNumber}
            />
          </li>
        ))}
      </ul>

      <SurahDetail entry={selectedEntry} />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <LegendSwatch className="border-border bg-muted/60" label="Belum ada target" />
        <LegendSwatch className="border-primary/30 bg-primary/10" label="Ada target" />
        <LegendSwatch className="border-primary/60 bg-primary/40" label="Sebagian selesai" />
        <LegendSwatch className="border-primary bg-primary" label="Seluruh surah selesai" />
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground">
        Peta ini menggambarkan rentang ayat yang <strong>ditargetkan</strong> untuk santri ini dan
        mana yang berstatus selesai - bukan verifikasi hafalan yang berdiri sendiri. Angka pasti per
        target ada di halaman target masing-masing. Struktur surah &amp; jumlah ayat berasal dari
        data rujukan Qur&apos;an di platform ini, bukan dari layanan teks Qur&apos;an eksternal.
      </p>
    </>
  );
}

function LegendSwatch({
  className,
  label,
}: {
  readonly className: string;
  readonly label: string;
}): JSX.Element {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`size-3 rounded-sm border ${className}`} aria-hidden="true" />
      {label}
    </span>
  );
}
